<script setup lang="ts">
import type { MenuItem } from '@global-types/nav/menuItem';
import { computed } from 'vue';
import eventBus from '@elements/tools/event-bus';
import Icon from '@components/Icon.vue';
import Typography from '@components/Typography.vue';
import MobileVerticalMenuButton from './MobileVerticalMenuButton.vue';
import MobileVerticalMenuLink from './MobileVerticalMenuLink.vue';

interface MenuDropdownProps {
  icon: string,
  iconName?: string,
  hasIconStroke?: boolean,
  isOpen: boolean,
  submenus: MenuItem[],
  isProfileManagement?: boolean;
  hasKycActive?: boolean;
  hasPayBuyer?: boolean;
  hasSubmenusBorder?: boolean;
}

const props = withDefaults(defineProps<MenuDropdownProps>(), {
  isOpen: false,
  icon: '',
  submenus: () => [],
  isProfileManagement: false,
  hasKycActive: false,
  hasPayBuyer: false,
  hasSubmenusBorder: false,
});

const submitEvent = (event: string) => {
  eventBus.dispatchEvent(new CustomEvent(event));
};
const displayedSubmenus = computed<MenuItem[]>(() => props.submenus.filter((submenu) => (
  (!submenu.isProfileManagement || props.isProfileManagement)
    && (!submenu.hasKycActive || props.hasKycActive)
    && (!('requiresPayBuyer' in submenu) || !submenu.requiresPayBuyer || !!props.hasPayBuyer)
)));
</script>

<template>
  <div class="menu-dropdown">
    <button
      class="menu-item"
      :class="[ { 'menu-item--active': isOpen }, typeof iconName === 'string' ? iconName : '' ]"
      @click="$emit('toggle-menu')"
    >
      <Icon
        v-if="hasIconStroke"
        :svg="icon"
        :custom-class="iconName"
        height="24"
        width="24"
        fill="none"
        stroke="gray-darker"
      />
      <Icon
        v-else
        :svg="icon"
        :custom-class="iconName"
        height="24"
        width="24"
        fill="gray-darker"
      />

      <Typography :variant="isOpen ? 'body-bold' : 'body'">
        <slot />
      </Typography>

      <span
        class="menu-dropdown__icon"
        :class="`menu-dropdown__icon--caret-${isOpen ? 'up' : 'down'}`"
      />
    </button>

    <ul
      v-if="isOpen"
      class="menu-dropdown__submenus"
      :class="typeof iconName === 'string' ? iconName : ''"
    >
      <li
        v-for="submenu in displayedSubmenus"
        :key="submenu.name"
      >
        <MobileVerticalMenuButton
          v-if="submenu.event"
          :icon="submenu.icon"
          :icon-name="submenu.iconName"
          :has-icon-stroke="submenu.hasIconStroke"
          @click="submitEvent(submenu.event)"
        >
          {{ submenu.name }}
        </MobileVerticalMenuButton>

        <MobileVerticalMenuLink
          v-else
          :class="submenu.class"
          :link="submenu.link"
          :icon="submenu.icon"
          :icon-name="submenu.iconName"
          :has-icon-stroke="submenu.hasIconStroke"
        >
          {{ submenu.name }}
        </MobileVerticalMenuLink>

        <ul
          v-if="submenu.submenus?.length"
          class="menu-dropdown__submenus"
          :class="{ 'menu-dropdown-submenus__border': hasSubmenusBorder }"
        >
          <li
            v-for="lowerSubmenu in submenu.submenus"
            :key="lowerSubmenu.name"
            class="menu-dropdown-submenus__sublink"
          >
            <MobileVerticalMenuLink
              v-if="lowerSubmenu.link"
              :link="lowerSubmenu.link"
              size="small"
            >
              {{ lowerSubmenu.name }}
            </MobileVerticalMenuLink>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>
